import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { GUIDES, getGuide } from '../guides';
import PageMeta from '../PageMeta';

const SITE_URL = 'https://propdoc.site';

/* Article structured data for the current guide; removed on unmount so it
   never leaks onto other routes. */
function useArticleJsonLd(guide) {
  useEffect(() => {
    if (!guide) return undefined;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: guide.title,
      description: guide.description,
      datePublished: guide.published,
      author: { '@type': 'Organization', name: 'Prop Doc', url: SITE_URL },
      publisher: { '@type': 'Organization', name: 'Prop Doc', url: SITE_URL },
      mainEntityOfPage: `${SITE_URL}/guides/${guide.slug}`,
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [guide]);
}

function GuideCta() {
  return (
    <aside className="guide-cta">
      <div>
        <h3>Let Prop Doc read it first</h3>
        <p>
          Upload your Section 32 or Contract of Sale and get the risks, easements, fees
          and agent questions in plain English — in about two minutes.
        </p>
      </div>
      <Link to="/analyze" className="btn-cta-primary">
        Analyse a document
      </Link>
    </aside>
  );
}

export function GuidesIndex() {
  return (
    <main className="guides-page">
      <PageMeta
        title="Section 32 guides for Victorian buyers — Prop Doc"
        path="/guides"
        description="Plain-English guides to Victorian Section 32 vendor statements: what the document is, a pre-signing checklist, and how to read easements on your plan."
      />
      <section className="guides-hero">
        <span className="hero-eyebrow">Guides</span>
        <h1>Understand what you're signing</h1>
        <p>
          Plain-English guides to the documents behind every Victorian property purchase
          — written for buyers, not lawyers.
        </p>
      </section>
      <section className="guides-list">
        {GUIDES.map((g) => (
          <Link key={g.slug} to={`/guides/${g.slug}`} className="guide-card">
            <h2>{g.navTitle}</h2>
            <p>{g.teaser}</p>
            <span className="guide-card-meta">{g.readingTime}</span>
          </Link>
        ))}
      </section>
    </main>
  );
}

export function GuidePage() {
  const { slug } = useParams();
  const guide = getGuide(slug);
  useArticleJsonLd(guide);

  if (!guide) return <Navigate to="/guides" replace />;

  const { Component } = guide;
  const others = GUIDES.filter((g) => g.slug !== guide.slug);

  return (
    <main className="guides-page">
      <PageMeta
        title={`${guide.title} — Prop Doc`}
        path={`/guides/${guide.slug}`}
        description={guide.description}
      />
      <article className="guide-article">
        <nav className="guide-breadcrumb" aria-label="Breadcrumb">
          <Link to="/guides">Guides</Link>
          <span aria-hidden="true">/</span>
          <span>{guide.navTitle}</span>
        </nav>
        <header className="guide-head">
          <h1>{guide.title}</h1>
          <p className="guide-byline">
            Updated {formatDate(guide.published)} · {guide.readingTime}
          </p>
        </header>
        <div className="guide-prose">
          <Component />
        </div>
        <p className="guide-disclaimer">
          This guide is general information for Victorian property buyers, not legal
          advice. Always have a qualified conveyancer or solicitor review the documents
          for your specific purchase.
        </p>
        <GuideCta />
        <section className="guide-more">
          <h3>Keep reading</h3>
          <div className="guides-list">
            {others.map((g) => (
              <Link key={g.slug} to={`/guides/${g.slug}`} className="guide-card">
                <h2>{g.navTitle}</h2>
                <p>{g.teaser}</p>
                <span className="guide-card-meta">{g.readingTime}</span>
              </Link>
            ))}
          </div>
        </section>
      </article>
    </main>
  );
}

function formatDate(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
